// Cookie-consent helper — mirrors the cash-landing funnel `consent.ts`.
//
// Reads the CookieYes consent cookie and lets callers run code only once the
// user has granted the advertisement category (or fire immediately if it is
// already granted, e.g. a returning visitor or consent shared from the landing
// via CookieYes subdomain sharing). Nothing here touches trackers directly —
// each caller gates itself with onConsent()/hasConsent().

export const CONSENT_CATEGORY = "advertisement";
const CONSENT_COOKIE = "cookieyes-consent";
const CONSENT_EVENT = "cookieyes_consent_update";

// CookieYes writes the cookie as soon as it loads, with every category in its
// configured default state and `action:no` until the user answers the banner.
// Those defaults are not consent, so a decision is required before we read the
// categories at all.
const DECIDED = /(?:^|,)\s*action:yes(?:\s*,|$)/;

// Consent is enforced ONLY on the domain where CookieYes actually runs — the
// mundijuegos.cash registrable domain (i.e. webgl.mundijuegos.cash for /play).
// On any other host (the legacy .com, alpha/test, localhost) CookieYes never
// loads, so we do NOT gate: cookies fire as before and attribution stays alive.
// The moment /play is served from webgl.mundijuegos.cash, gating turns on by
// itself and the real CookieYes decision (accept/reject) is respected.
function consentEnforced() {
  return typeof window !== "undefined" && /(^|\.)mundijuegos\.cash$/.test(window.location.hostname);
}

/** Raw CookieYes consent cookie, or "" when CookieYes never wrote one. */
export function getConsentRaw() {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : "";
}

/**
 * Whether the advertisement category is granted. Off the gated domain this always
 * returns true (no gating). On the gated domain it reflects the CookieYes consent
 * cookie (`...,action:yes,...,advertisement:yes,...`) — no answer yet and a reject
 * both stay a no.
 */
export function hasConsent(category = CONSENT_CATEGORY) {
  if (!consentEnforced()) return true; // not the CookieYes domain → do not gate
  const raw = getConsentRaw();
  if (!raw) return false;
  if (!DECIDED.test(raw)) return false; // banner still unanswered
  return new RegExp(`(?:^|,)\\s*${category}:yes(?:\\s*,|$)`).test(raw);
}

/**
 * Run `cb` once the category is consented: immediately if already granted, else
 * when CookieYes fires its `cookieyes_consent_update` event with the category on.
 */
export function onConsent(cb, category = CONSENT_CATEGORY) {
  if (typeof document === "undefined") return;
  if (hasConsent(category)) {
    cb();
    return;
  }
  const handler = () => {
    if (!hasConsent(category)) return;
    document.removeEventListener(CONSENT_EVENT, handler);
    cb();
  };
  document.addEventListener(CONSENT_EVENT, handler);
}
